import { Step, StepLabel, Stepper, Typography } from '@mui/material';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import theme from 'theme';
import {
  ONBOARDING__ACCOUNT_STEP,
  ONBOARDING__ADDRESS_STEP,
  ONBOARDING__ALREADY_STARTED_MESSAGE,
  ONBOARDING__COMPANY_STEP,
  ONBOARDING__CONTACT_STEP,
} from 'translations/constants';

import { UPToggleButton } from 'components/button/styles';
import UPButtonGroup from 'components/button/UPGroupButton';
import GeneralConfirmModal from 'components/general-confirm-modal';
import { useAppDispatch } from 'store';
import { getOnboardingCompanyData } from 'store/actions/company-onboarding-actions';
import { getCountriesNamesAction } from 'store/actions/countries-actions';
import Lang from 'types/Lang';
import { getLanguageId } from 'utils/helpers';
import AccountStep from './steps/AccountStep';
import AddressStep from './steps/AddressStep';
import CompanyStep from './steps/CompanyStep';
import ContactStep from './steps/ContactStep';
import StepIconComponent from './steps/StepIconComponent';
import {
  OnboardingFlowContainer,
  OnboardingFlowLanguageSelector,
  OnboardingFlowScrollableContainer,
  OnboardingFlowStepConnector,
  OnboardingFlowStepperContainer,
  OnboardingImage,
} from './styles';
import { OnboardingData, OnboardingFlowContextProps } from './types';

export const OnboardingFlowContext = createContext<OnboardingFlowContextProps>({
  t: null,
  activeStep: 0,
  onClickPreviousStep: () => null,
  onClickNextStep: () => null,
  onboardingData: null,
  setOnboardingData: () => null,
  setModalOpen: () => null,
  companyIdNumber: null,
  setCompanyIdNumber: () => null,
  languageId: 1,
  isExistingManager: false,
  setIsExistingManager: () => null,
});

const OnboardingFlow: React.FC<{ isInitiated: boolean }> = ({ isInitiated }) => {
  const [t, i18n] = useTranslation();
  const dispatch = useAppDispatch();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [language, setLanguage] = useState<Lang>(i18n.language as Lang);
  const [companyIdNumber, setCompanyIdNumber] = useState<number>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isExistingManager, setIsExistingManager] = useState(false);

  const steps = [
    t(ONBOARDING__COMPANY_STEP),
    t(ONBOARDING__ADDRESS_STEP),
    t(ONBOARDING__CONTACT_STEP),
    t(ONBOARDING__ACCOUNT_STEP),
  ];

  const onClickPreviousStep = useCallback(() => {
    setActiveStep(activeStep - 1);
  }, [activeStep]);

  const onClickNextStep = useCallback(() => {
    setActiveStep(activeStep + 1);
  }, [activeStep]);

  const contextValue = useMemo(
    (): OnboardingFlowContextProps => ({
      t,
      activeStep,
      onClickPreviousStep,
      onClickNextStep,
      onboardingData,
      setOnboardingData,
      setModalOpen,
      companyIdNumber,
      setCompanyIdNumber,
      languageId: getLanguageId(language),
      isExistingManager,
      setIsExistingManager,
    }),
    [t, activeStep, onClickPreviousStep, onClickNextStep, onboardingData, companyIdNumber, language, isExistingManager],
  );

  const onChangeLanguage = (value: string) => {
    const lang = value as Lang;
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    setLanguage(i18n.language as Lang);
    dispatch(getCountriesNamesAction(language));
  }, [dispatch, i18n.language, language]);

  const onClickContinue = async () => {
    const companyData = (await dispatch(getOnboardingCompanyData(companyIdNumber))).payload as OnboardingData;
    setOnboardingData({
      ...onboardingData,
      company: {
        ...companyData.company,
        differentCommercialName: companyData.company.commercialName !== companyData.company.name,
      },
      address: {
        streetName: companyData.address.streetName || '',
        houseNumber: companyData.address.houseNumber || '',
        postBox: companyData.address.postBox || '',
        zipCode: companyData.address.zipCode || '',
        city: companyData.address.city || '',
        countryId: companyData.address.countryId > 0 ? companyData.address.countryId : null,
      },
    });
    setModalOpen(false);
    onClickNextStep();
  };

  const onClickCancel = () => {
    setCompanyIdNumber(null);
    setModalOpen(false);
  };

  return (
    <OnboardingFlowContainer hidden={!isInitiated}>
      <OnboardingImage flow />
      <OnboardingFlowLanguageSelector>
        <UPButtonGroup label="" value={language} onChange={onChangeLanguage}>
          <UPToggleButton value="en" disabled={language === Lang.en}>
            en
          </UPToggleButton>
          <UPToggleButton value="fr" disabled={language === Lang.fr}>
            fr
          </UPToggleButton>
          <UPToggleButton value="nl" disabled={language === Lang.nl}>
            nl
          </UPToggleButton>
        </UPButtonGroup>
      </OnboardingFlowLanguageSelector>
      <OnboardingFlowStepperContainer>
        <Stepper orientation="vertical" activeStep={activeStep} connector={<OnboardingFlowStepConnector />}>
          {steps.map((step, index) => (
            <Step key={`${step}-${index.toString()}`}>
              <StepLabel
                icon={<StepIconComponent activeStep={activeStep} index={index} setActiveStep={setActiveStep} />}
              >
                <Typography
                  fontWeight={600}
                  color={activeStep >= index ? theme.palette.primary.main : theme.palette.secondary.light}
                >
                  {step}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </OnboardingFlowStepperContainer>
      <OnboardingFlowContext.Provider value={contextValue}>
        <OnboardingFlowScrollableContainer>
          <CompanyStep />
          <AddressStep />
          <ContactStep />
          <AccountStep />
        </OnboardingFlowScrollableContainer>
      </OnboardingFlowContext.Provider>
      {modalOpen && (
        <GeneralConfirmModal
          open
          onClose={onClickCancel}
          content={t(ONBOARDING__ALREADY_STARTED_MESSAGE)}
          onAction={onClickContinue}
        />
      )}
    </OnboardingFlowContainer>
  );
};

export default OnboardingFlow;
